import React from 'react';
import { useNarrowScreen } from '../hooks/useNarrowScreen';

interface StatusCardMeta {
  /** 状态码；undefined 表示「全部」 */
  value?: string;
  label: string;
  en: string;
  color: string;
}

/** 卡片顺序与列表筛选 statusOptions 一致 */
const CARDS: StatusCardMeta[] = [
  { value: undefined, label: '全部', en: 'All', color: '#595959' },
  { value: 'PRE_DECLARATION', label: '预申报', en: 'Pre-Declaration', color: '#8c8c8c' },
  { value: 'SUBMITTED_TO_DEPOT', label: '已交代理仓', en: 'Submitted to Depot', color: '#13c2c2' },
  { value: 'HANDOVER_CONFIRMING', label: '交货确认中', en: 'Handover Confirming', color: '#fa8c16' },
  { value: 'CONFIRMED_WAIT_DEPARTURE', label: '准备运输', en: 'Awaiting Departure', color: '#faad14' },
  { value: 'IN_TRANSIT', label: '运输中', en: 'In Transit', color: '#1677ff' },
  { value: 'OUT_FOR_DELIVERY', label: '待派送', en: 'Out for Delivery', color: '#722ed1' },
  { value: 'DELIVERED', label: '已签收', en: 'Delivered', color: '#52c41a' },
  { value: 'CANCELLED', label: '已撤销', en: 'Cancelled', color: '#ff4d4f' },
];

interface Props {
  /** 状态码 → 运单数（全局统计） */
  counts: Record<string, number>;
  /** 「全部」显示数（不含已撤销） */
  totalAll: number;
  /** 当前生效的状态筛选（undefined = 全部） */
  activeStatus?: string;
  onSelect: (status?: string) => void;
}

/**
 * 运单列表顶部状态卡片条（横向）：
 * 每卡 = 中文状态 + 大号彩色计数 + 英文小标签；点击卡片立即筛选主列表，
 * 选中卡片带状态色边框高亮，再点一次取消筛选回到「全部」。
 */
const ShipmentStatusCards: React.FC<Props> = ({ counts, totalAll, activeStatus, onSelect }) => {
  const narrow = useNarrowScreen();
  return (
    <div
      style={
        narrow
          ? {
              display: 'grid',
              gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
              gap: 6,
              marginBottom: 12,
            }
          : { display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 12 }
      }
    >
      {CARDS.map((c) => {
        const active = (activeStatus ?? undefined) === c.value;
        const count = c.value === undefined ? totalAll : counts[c.value] ?? 0;
        return (
          <div
            key={c.value ?? '__ALL__'}
            role="button"
            aria-pressed={active}
            onClick={() => onSelect(active && c.value !== undefined ? undefined : c.value)}
            style={{
              flex: narrow ? undefined : '1 1 100px',
              minWidth: narrow ? 0 : 100,
              maxWidth: narrow ? undefined : 200,
              minHeight: narrow ? 40 : undefined,
              display: 'flex',
              alignItems: 'center',
              justifyContent: narrow ? 'center' : undefined,
              gap: narrow ? 4 : 6,
              padding: narrow ? '4px 6px' : '4px 10px',
              border: `1px solid ${active ? c.color : '#e5e5e5'}`,
              borderRadius: 8,
              background: active ? `${c.color}14` : '#fff',
              boxShadow: active ? `0 0 0 1px ${c.color}` : 'none',
              cursor: 'pointer',
              userSelect: 'none',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
            }}
          >
            <span
              style={{
                fontSize: narrow ? 12 : 13,
                color: '#333',
                fontWeight: active ? 600 : 500,
                minWidth: 0,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {c.label}
            </span>
            <span style={{ fontSize: narrow ? 13 : 16, fontWeight: 600, color: c.color, flexShrink: 0 }}>{count}</span>
            {!narrow && (
              <span style={{ fontSize: 11, color: '#999', overflow: 'hidden', textOverflow: 'ellipsis' }}>{c.en}</span>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default ShipmentStatusCards;
